'use client'

import { useTranslations } from 'next-intl'
import { ComponentProps, KeyboardEvent, useRef } from 'react'

import { cn, Slot } from '@/shared/lib'
import { Button, Icon, TextField } from '@/shared/ui'

type Props = Omit<ComponentProps<'div'>, 'onChange'> & {
  value: string
  onChange: (value: string) => void
  onSearch: () => void
  onReset?: () => void
  placeholder?: string
}

export const SearchBar = ({
  value,
  onChange,
  onSearch,
  onReset,
  placeholder = '',
  className,
  ...rest
}: Props) => {
  const t = useTranslations('common')

  const inputRef = useRef<HTMLInputElement>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch()
    }
  }

  const handleInputReset = () => {
    onChange('')
    onReset?.()
  }

  return (
    <div
      className={cn(
        'flex w-[449px] shrink-0 grow items-center gap-[1px] pl-[5px]',
        className,
      )}
      {...rest}
    >
      <TextField
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onReset={handleInputReset}
        placeholder={placeholder}
        className="h-[50px] w-full border-none bg-[rgba(0,0,0,0)] text-[15px]! leading-[22px]! *:placeholder:text-gray-400"
        fullWidth
      >
        <Slot name="left">
          <Icon
            name="search"
            size={24}
            className="text-black"
            strokeWidth={1.5}
          />
        </Slot>
      </TextField>
      <Button
        size="small"
        weight="normal"
        className="w-[50px]"
        onClick={onSearch}
      >
        {t('search.button')}
      </Button>
    </div>
  )
}
